import { authenticatedFetch } from './authenticatedFetch';

/**
 * THRESHOLDS API MODULE - Medical Alert Threshold Management System
 * 
 * This module provides comprehensive management of medical alert thresholds
 * for vital signs monitoring. It defines standard medical thresholds based
 * on clinical guidelines and allows for patient-specific customization.
 * 
 * Key Features:
 * - Standard medical thresholds based on clinical guidelines
 * - Patient-specific threshold customization
 * - Multi-level alert system (low, medium, high, critical)
 * - Blood pressure stage classification (Stage 1, 2, 3)
 * - Heart rate variability monitoring thresholds
 * - Temperature fever and hypothermia detection
 * - Oxygen saturation critical level monitoring
 * 
 * Medical Standards:
 * - Heart Rate: Normal (60-100 BPM), Bradycardia (<60), Tachycardia (>100)
 * - Blood Pressure: Normal, Stage 1, Stage 2, Stage 3 hypertension
 * - Temperature: Normal (36-37.3°C), Fever (>37.3°C), High fever (>39.1°C)
 * - SpO2: Normal (>94%), Low (90-94%), Critical (<90%)
 * - Respiratory Rate: Normal (12-20 BPM), Abnormal (<12 or >20)
 * - HRV: Individual variability based on patient baseline
 */

/**
 * THRESHOLDS TYPE - Medical Alert Threshold Configuration
 * 
 * Defines the complete threshold structure for all vital signs monitoring.
 * Each vital sign has multiple threshold levels for graduated alerting.
 */
export type Thresholds = {
  heartRate: { low: number; medium: number; high: number };                    // Heart rate thresholds in BPM
  hrv: { high: number; medium: number; low: number };                         // Heart rate variability thresholds in ms
  spo2: { medium: number; low: number };                                     // Oxygen saturation thresholds in %
  breathing: { low: number; medium: number; high: number };                   // Respiratory rate thresholds in BPM
  bloodPressure: {
    systolic: { low:number; systolic_stage1: number; systolic_stage2: number; systolic_stage3: number ;high: number };    // Systolic BP thresholds in mmHg
    diastolic: { low: number; diastolic_stage1: number; diastolic_stage2: number; diastolic_stage3: number; high: number }; // Diastolic BP thresholds in mmHg
  };
  temperature: { low: number; medium: number; high: number };                // Temperature thresholds in Celsius
};

const STORAGE_KEY = 'thresholds_v1';

export const defaultThresholds: Thresholds = {
  heartRate: { low: 45, medium: 100, high: 130 },
  hrv: { high: 200, medium: 25, low: 15 },
  spo2: { medium: 94, low: 90 },
  breathing: { low: 8, medium: 20, high: 24 },
  bloodPressure: {
    systolic: { low: 90, systolic_stage1: 130, systolic_stage2: 140, systolic_stage3: -1, high: 180 },
    diastolic: { low: 60, diastolic_stage1: 80, diastolic_stage2: 90, diastolic_stage3: -1, high: 120 },
  },
  temperature: { low: 29, medium: 35, high: 38 },
};

// Cache for patient thresholds to avoid repeated API calls
let thresholdsCache: { [patId: string]: Thresholds } = {};
let lastPatId: string | null = null;

export async function loadThresholds(patId?: string): Promise<Thresholds> {
  try {
    // Get pat_id from parameter or localStorage
    const currentPatId = patId || localStorage.getItem('selectedPatId');
    
    if (!currentPatId) {
      console.log('[thresholds] No pat_id available, using default thresholds');
      return defaultThresholds;
    }

    // Check cache first
    if (thresholdsCache[currentPatId] && lastPatId === currentPatId) {
      console.log('[thresholds] Using cached thresholds for pat_id:', currentPatId);
      return thresholdsCache[currentPatId];
    }

    console.log('[thresholds] Fetching thresholds from API for pat_id:', currentPatId);
    
    // Fetch from API
    const response = await authenticatedFetch(`/api/patient-thresholds?pat_id=${currentPatId}`);
    
    if (!response.ok) {
      console.warn('[thresholds] Failed to fetch patient thresholds, using defaults:', response.status);
      return defaultThresholds;
    }

    const patientThresholds = await response.json();
    
    // Cache the result
    thresholdsCache[currentPatId] = patientThresholds;
    lastPatId = currentPatId;
    
    console.log('[thresholds] Loaded patient thresholds:', patientThresholds);
    return patientThresholds;
    
  } catch (error) {
    console.error('[thresholds] Error loading thresholds:', error);
    return defaultThresholds;
  }
}

// Legacy function for backward compatibility - now returns default thresholds
export function loadThresholdsSync(): Thresholds {
  console.warn('[thresholds] loadThresholdsSync is deprecated, use loadThresholds() instead');
  return defaultThresholds;
}

export async function saveThresholds(t: Thresholds, patId?: string): Promise<boolean> {
  try {
    const currentPatId = patId || localStorage.getItem('selectedPatId');
    
    if (!currentPatId) {
      console.warn('[thresholds] No pat_id available for saving thresholds');
      // Fallback to localStorage
      localStorage.setItem(STORAGE_KEY, JSON.stringify(t));
      return true;
    }

    console.log('[thresholds] Saving thresholds for pat_id:', currentPatId);
    
    const response = await authenticatedFetch('/api/patient-thresholds', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        pat_id: currentPatId,
        thresholds: t
      })
    });

    if (!response.ok) {
      console.error('[thresholds] Failed to save thresholds:', response.status);
      // Fallback to localStorage
      localStorage.setItem(STORAGE_KEY, JSON.stringify(t));
      return false;
    }

    const result = await response.json();
    console.log('[thresholds] Thresholds saved successfully:', result);
    
    // Clear cache to force reload on next access
    clearThresholdsCache();
    
    return true;
  } catch (error) {
    console.error('[thresholds] Error saving thresholds:', error);
    // Fallback to localStorage
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(t));
    } catch {}
    return false;
  }
}

// Clear cache when patient changes
export function clearThresholdsCache(): void {
  thresholdsCache = {};
  lastPatId = null;
}

// -----------------------------------------------------------------------------
// Doctor preset thresholds (settings): default thresholds for all patients
// assigned to this doctor. Used when the doctor sets thresholds in Settings.
// -----------------------------------------------------------------------------

/**
 * Load the doctor's preset thresholds by doc_id.
 * Returns null if none are set (404). The caller can fall back to defaultThresholds.
 */
export async function getDoctorPresetThresholds(docId: string | number): Promise<Thresholds | null> {
  try {
    const id = typeof docId === 'number' ? String(docId) : docId;
    const response = await authenticatedFetch(`/api/doctor-preset-thresholds?doc_id=${encodeURIComponent(id)}`);
    if (response.status === 404) return null;
    if (!response.ok) {
      console.warn('[thresholds] getDoctorPresetThresholds failed:', response.status);
      return null;
    }
    const data = await response.json();
    return data as Thresholds;
  } catch (error) {
    console.error('[thresholds] getDoctorPresetThresholds error:', error);
    return null;
  }
}

/**
 * Save the doctor's preset thresholds. These become the default for all patients
 * the doctor assigns to themselves.
 */
export async function saveDoctorPresetThresholds(docId: string | number, t: Thresholds): Promise<boolean> {
  try {
    const id = typeof docId === 'number' ? String(docId) : docId;
    const response = await authenticatedFetch('/api/doctor-preset-thresholds', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ doc_id: id, thresholds: t }),
    });
    if (!response.ok) {
      console.error('[thresholds] saveDoctorPresetThresholds failed:', response.status);
      return false;
    }
    return true;
  } catch (error) {
    console.error('[thresholds] saveDoctorPresetThresholds error:', error);
    return false;
  }
}

